import { useState, useEffect } from "react";
// @mui
import {
  Box,
  List,
  Alert,
  Stack,
  Button,
  Dialog,
  IconButton,
  Typography,
  DialogTitle,
  DialogContent,
  ListItemText,
  ListItemButton,
  ListItemAvatar,
  CircularProgress,
} from "@mui/material";
// auth
import { useAuthContext } from "../../auth/useAuthContext";
// components
import Iconify from "../../components/iconify";
import { CustomAvatar } from "../../components/custom-avatar";

// ----------------------------------------------------------------------

// Rendered wherever it is mounted; opens when the auth context has a pending venue selection
// (after a multi-venue login, or via openVenueSwitcher() for an authenticated user).
export default function VenueSelectionModal() {
  const { user, venueSelection, selectVenue, switchVenue, closeVenueSelection } = useAuthContext();

  const [selectingId, setSelectingId] = useState(null);
  const [error, setError] = useState(null);

  const open = !!venueSelection;
  const closable = !!venueSelection?.closable;
  const venues = venueSelection?.venues || [];
  const currentVenueId = closable ? user?.venueId : null;

  useEffect(() => {
    if (!open) {
      setSelectingId(null);
      setError(null);
    }
  }, [open]);

  const handleClose = () => {
    if (closable && !selectingId) closeVenueSelection();
  };

  const handleSelect = async (venueId) => {
    if (selectingId) return;
    setSelectingId(venueId);
    setError(null);
    try {
      // Login uses the one-time selection token; switching an authenticated session uses switch-venue.
      await (closable ? switchVenue(venueId) : selectVenue(venueId));
    } catch (err) {
      console.error(err);
      setError(err?.code === 500 ? "Something went wrong, Please try later" : err?.message || "Unable to select venue");
      setSelectingId(null);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={!closable}
    >
      <DialogTitle sx={{ pb: 1, pr: closable ? 6 : 3 }}>
        {closable ? "Switch branch" : "Select a branch"}
        {closable && (
          <IconButton
            onClick={handleClose}
            disabled={!!selectingId}
            aria-label="Close"
            sx={{ position: "absolute", top: 12, right: 12 }}
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {closable
            ? "Choose the branch you want to continue in."
            : "Your account has access to more than one branch. Choose one to continue."}
        </Typography>

        {!!error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              !closable && (
                <Button color="inherit" size="small" onClick={() => closeVenueSelection(true)}>
                  Back to login
                </Button>
              )
            }
          >
            {error}
          </Alert>
        )}

        <List disablePadding>
          {venues.map((venue) => {
            const isCurrent = !!currentVenueId && venue.id === currentVenueId;
            const isSelecting = selectingId === venue.id;

            return (
              <ListItemButton
                key={venue.id}
                disabled={isCurrent || (!!selectingId && !isSelecting)}
                onClick={() => handleSelect(venue.id)}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  border: (theme) => `solid 1px ${theme.palette.divider}`,
                }}
              >
                <ListItemAvatar>
                  <CustomAvatar src={venue.imageURL} alt={venue.name} name={venue.name} />
                </ListItemAvatar>

                <ListItemText
                  primary={venue.name}
                  secondary={venue.role?.name}
                  primaryTypographyProps={{ typography: "subtitle2", noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />

                <Box sx={{ ml: 1, display: "flex", alignItems: "center" }}>
                  {isSelecting && <CircularProgress size={20} />}
                  {!isSelecting && isCurrent && (
                    <Typography variant="caption" color="text.secondary">
                      Current
                    </Typography>
                  )}
                  {!isSelecting && !isCurrent && (
                    <Iconify icon="eva:arrow-ios-forward-fill" sx={{ color: "text.disabled" }} />
                  )}
                </Box>
              </ListItemButton>
            );
          })}
        </List>

        {!venues.length && (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No venues available.
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
